import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Trash2, 
  Edit2, 
  UserPlus, 
  Users,
  Search as SearchIcon,
  Filter,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  X,
  CreditCard,
  Building2,
  User,
  Loader2,
  ArrowRightLeft,
  Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ManagedContact, ContactIdType } from '../../types';
import { subscribeToContacts, createContact, updateContact, deleteContact } from '../../services/managementService';

interface ContactManagerProps {
  userId: string;
}

export const ContactManager: React.FC<ContactManagerProps> = ({ userId }) => {
  const [contacts, setContacts] = useState<ManagedContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'CUSTOMER' | 'PROVIDER'>('ALL');
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<ManagedContact | null>(null);

  useEffect(() => {
    const unsub = subscribeToContacts(userId, (data) => {
      setContacts(data);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(search.toLowerCase()) ||
      contact.taxId.toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = 
      filterType === 'ALL' ||
      (filterType === 'CUSTOMER' && contact.isCustomer) ||
      (filterType === 'PROVIDER' && contact.isProvider);
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="text-emerald-500" /> Directorio de Contactos
          </h2>
          <p className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Clientes y Proveedores</p>
        </div>
        <button 
          onClick={() => {
            setEditingContact(null);
            setShowForm(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20"
        >
          <UserPlus size={20} /> Nuevo Contacto
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
          <input 
            type="text"
            placeholder="Buscar por nombre o nro. documento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-2xl pl-12 pr-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
          />
        </div>
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-2xl p-1">
          {(['ALL', 'CUSTOMER', 'PROVIDER'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${filterType === t ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              {t === 'ALL' ? 'Todos' : t === 'CUSTOMER' ? 'Clientes' : 'Proveedores'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full py-20 flex flex-col items-center justify-center space-y-4">
            <Loader2 className="animate-spin text-emerald-500" size={48} />
            <p className="text-zinc-500 font-bold uppercase tracking-widest text-xs">Cargando contactos...</p>
          </div>
        ) : filteredContacts.map(contact => (
          <motion.div 
            layout
            key={contact.id}
            className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 hover:border-emerald-500/50 transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center border border-zinc-700">
                {contact.isCustomer && contact.isProvider ? <ArrowRightLeft size={20} className="text-amber-500" /> : contact.isCustomer ? <User size={20} className="text-emerald-500" /> : <Building2 size={20} className="text-blue-500" />}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    setEditingContact(contact);
                    setShowForm(true);
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-xl text-zinc-500 hover:text-white transition-all"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  onClick={() => {
                    if(window.confirm(`¿Seguro que deseas eliminar a ${contact.name}?`)) deleteContact(userId, contact.id);
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-xl text-zinc-500 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <h3 className="text-lg font-bold text-white mb-1">{contact.name}</h3>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-md font-bold uppercase tracking-tighter">
                {contact.taxIdType}
              </span>
              <span className="text-xs text-zinc-500 font-mono">{contact.taxId}</span>
            </div>

            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <Mail size={14} className="text-zinc-600" />
                  <span className="truncate">{contact.email}</span>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <Phone size={14} className="text-zinc-600" />
                  <span>{contact.phone}</span>
                </div>
              )}
              {contact.address && (
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <MapPin size={14} className="text-zinc-600" />
                  <span className="truncate">{contact.address}</span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800 flex flex-wrap gap-2">
              {contact.isCustomer && (
                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-500 text-[10px] font-bold rounded-full border border-emerald-500/20">
                  CLIENTE
                </span>
              )}
              {contact.isProvider && (
                <span className="px-3 py-1 bg-blue-500/10 text-blue-500 text-[10px] font-bold rounded-full border border-blue-500/20">
                  PROVEEDOR
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showForm && (
          <ContactFormModal 
            userId={userId}
            contact={editingContact}
            onClose={() => setShowForm(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ContactFormModal: React.FC<{ userId: string; contact: ManagedContact | null; onClose: () => void }> = ({ userId, contact, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Omit<ManagedContact, 'id' | 'createdAt' | 'userId'>>({
    name: contact?.name || '',
    taxIdType: contact?.taxIdType || 'RUC',
    taxId: contact?.taxId || '',
    isCustomer: contact?.isCustomer ?? true,
    isProvider: contact?.isProvider ?? false,
    email: contact?.email || '',
    phone: contact?.phone || '',
    address: contact?.address || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (contact) {
        await updateContact(userId, contact.id, formData);
      } else {
        await createContact(userId, formData);
      }
      onClose();
    } catch (error) {
      alert("Error al guardar el contacto.");
    } finally {
      setLoading(false);
    }
  };

  const idTypes: ContactIdType[] = [
    'RUC', 'CÉDULA DE IDENTIDAD', 'PASAPORTE', 'CÉDULA EXTRANJERO', 
    'SIN NOMBRE', 'DIPLOMÁTICO', 'IDENTIFICACIÓN TRIBUTARIA', 'CLIENTE DEL EXTERIOR'
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[60] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-xl overflow-hidden"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {contact ? 'Editar Contacto' : 'Nuevo Contacto'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-xl transition-all">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Nombre / Razón Social</label>
              <input 
                required
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Tipo Identificación</label>
                <select 
                  value={formData.taxIdType}
                  onChange={e => setFormData({ ...formData, taxIdType: e.target.value as ContactIdType })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
                >
                  {idTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Nro. Identificación</label>
                <input 
                  required
                  value={formData.taxId}
                  onChange={e => setFormData({ ...formData, taxId: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-4 p-4 bg-zinc-950 border border-zinc-800 rounded-2xl">
              <label className="flex-1 flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={formData.isCustomer}
                  onChange={e => setFormData({ ...formData, isCustomer: e.target.checked })}
                  className="w-5 h-5 rounded-md border-zinc-800 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-sm font-bold text-zinc-300">Es Cliente</span>
              </label>
              <label className="flex-1 flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={formData.isProvider}
                  onChange={e => setFormData({ ...formData, isProvider: e.target.checked })}
                  className="w-5 h-5 rounded-md border-zinc-800 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm font-bold text-zinc-300">Es Proveedor</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Email</label>
                <input 
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Teléfono</label>
                <input 
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Dirección</label>
              <input 
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
              />
            </div>
          </div>

          <div className="pt-6 flex gap-4">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-2xl transition-all"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" /> : <Save size={20} />}
              Guardar Contacto
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
